import { useQuery, useInfiniteQuery, InfiniteData } from "@tanstack/react-query"
import { CancelledError } from "@tanstack/query-core"
import { queryClient } from "./constants"
import { PNApiOperations } from "./operations"
import { Sentry } from "@/telemetry/sentry"
import { trackSubscriptionSucceededIfNeeded } from "@/telemetry/subscription-succeeded"
import { atomWithQuery } from "jotai-tanstack-query"
import { GetUserResponseBody } from "./dto/users"
import { FetchActivitiesResponseBodyDTO } from "./dto/users"
import { isAuthReadyAtom } from "@/store/auth-state"
import { NetworkRequestIntent } from "./network-behavior"

export function prefetchCurrentUser(): void {
	queryClient
		.prefetchQuery({
			queryKey: ["user"],
			queryFn: PNApiOperations.getCurrentUser,
			staleTime: 60000,
		})
		.catch(Sentry.captureException)
}

export const useUserQuery = (retry = true, enabled = true) => {
	return useQuery({
		queryKey: ["user"],
		queryFn: PNApiOperations.getCurrentUser,
		retry,
		enabled,
	})
}

export const userQueryAtom = atomWithQuery<GetUserResponseBody>((get) => ({
	queryKey: ["user"],
	queryFn: PNApiOperations.getCurrentUser,
	enabled: get(isAuthReadyAtom),
}))

export const useUserActivitiesQuery = () => {
	return useInfiniteQuery<
		FetchActivitiesResponseBodyDTO,
		Error,
		InfiniteData<FetchActivitiesResponseBodyDTO, string | undefined>,
		string[],
		string | undefined
	>({
		queryKey: ["user/activities"],
		initialPageParam: undefined,
		queryFn: async ({ pageParam }) =>
			PNApiOperations.getUserActivities({
				size: 10,
				anchor: pageParam,
			}),
		getNextPageParam: (lastPage) => lastPage.anchor,
	})
}

export const refreshUserAndGet = async (
	networkIntent: NetworkRequestIntent = "background",
): Promise<GetUserResponseBody | undefined> => {
	try {
		await PNApiOperations.syncUserProfile(networkIntent)
	} catch (error) {
		console.warn("Failed to sync user profile.", error)
	}

	try {
		return await queryClient.fetchQuery({
			queryKey: ["user"],
			queryFn: PNApiOperations.getCurrentUser,
			staleTime: 0,
		})
	} catch (error) {
		if (error instanceof CancelledError) {
			return undefined
		}
		console.warn("Failed to fetch refreshed user profile.", error)
		Sentry.captureException(error)
		return undefined
	}
}

export const forceRefreshUser = async (
	networkIntent: NetworkRequestIntent = "background",
): Promise<void> => {
	await refreshUserAndGet(networkIntent)
}

export const registerUserRefreshOnTabReturn = (storageKey: string): void => {
	try {
		window.sessionStorage.setItem(storageKey, String(Date.now()))
	} catch (error) {
		console.warn("Failed to record billing return state.", error)
	}

	const handleReturn = () => {
		if (document.visibilityState !== "visible") {
			return
		}

		try {
			const pending = window.sessionStorage.getItem(storageKey)
			if (!pending) {
				window.removeEventListener("focus", handleReturn)
				document.removeEventListener("visibilitychange", handleReturn)
				return
			}
			window.sessionStorage.removeItem(storageKey)
		} catch (error) {
			console.warn("Failed to clear billing return state.", error)
		}

		window.removeEventListener("focus", handleReturn)
		document.removeEventListener("visibilitychange", handleReturn)
		void refreshUserAndGet().then((user) => trackSubscriptionSucceededIfNeeded(user))
	}

	window.addEventListener("focus", handleReturn)
	document.addEventListener("visibilitychange", handleReturn)
}
